export const CLOUD_TYPE = {
    AWS: 'aws',
    AZURE: 'azure',
    AZURE_LOCAL: 'azure_local',
    GCP: 'gcp',
    VSPHERE: 'rivermeadow_standalone',
    OPENSHIFT: 'openshift',
    OPENSTACK: 'openstack',
    NC2: 'nc2',
    HPE_VME: 'hpe_vme',
    HYPERV: 'hyperv',
};

export const VSPHERE_TYPES = {
    VSPHERE_DR: 'vsphere_dr',
    VSPHERE_OFFLINE_REPLICAS: 'vsphere_offline_replicas',
};

export const AZURE_TYPES = {
    AZURE_DR: 'azure_dr',
};

// Cloud types for dropdown selection
export const CLOUD_TYPES = [
    { value: CLOUD_TYPE.AWS, label: 'AWS' },
    { value: CLOUD_TYPE.AZURE, label: 'Azure' },
    { value: CLOUD_TYPE.AZURE_LOCAL, label: 'Azure Local' },
    { value: CLOUD_TYPE.GCP, label: 'Google Cloud' },
    { value: CLOUD_TYPE.HPE_VME, label: 'HPE VM Essentials' },
    { value: CLOUD_TYPE.HYPERV, label: 'Hyper-V' },
    { value: CLOUD_TYPE.NC2, label: 'Nutanix NC2' },
    { value: CLOUD_TYPE.OPENSHIFT, label: 'OpenShift' },
    { value: CLOUD_TYPE.OPENSTACK, label: 'OpenStack' },
    { value: CLOUD_TYPE.VSPHERE, label: 'vSphere' },
];

export const DOWNLOADABLE_CLOUD_TYPES = [
    CLOUD_TYPE.VSPHERE,
    VSPHERE_TYPES.VSPHERE_DR,
    VSPHERE_TYPES.VSPHERE_OFFLINE_REPLICAS,
    CLOUD_TYPE.OPENSHIFT,
    CLOUD_TYPE.OPENSTACK,
    CLOUD_TYPE.NC2,
    CLOUD_TYPE.HPE_VME,
    CLOUD_TYPE.HYPERV,
];

export const PUBLIC_CLOUD_TYPES = [CLOUD_TYPE.AWS, CLOUD_TYPE.AZURE, CLOUD_TYPE.AZURE_LOCAL, CLOUD_TYPE.GCP];

// Cloud types that require connection test
export const CONNECTION_TEST_CLOUD_TYPES = [CLOUD_TYPE.AZURE, CLOUD_TYPE.AZURE_LOCAL, CLOUD_TYPE.GCP, CLOUD_TYPE.AWS];

// Cloud types that support VM-level credentials (vSphere source)
export const VM_CREDENTIALS_CLOUD_TYPES = [CLOUD_TYPE.AWS, CLOUD_TYPE.AZURE, CLOUD_TYPE.AZURE_LOCAL, CLOUD_TYPE.GCP];

/**
 * Check if a cloud type requires downloadable appliance setup (token-based)
 * @param {string} cloudType - The cloud type to check
 * @returns {boolean} True if the cloud type is downloadable
 */
export const isDownloadableCloudType = (cloudType) => {
    return DOWNLOADABLE_CLOUD_TYPES.includes(cloudType);
};

/**
 * Check if a cloud type is a public cloud (AWS, Azure, GCP)
 * @param {string} cloudType - The cloud type to check
 * @returns {boolean} True if the cloud type is a public cloud
 */
export const isPublicCloudType = (cloudType) => {
    return PUBLIC_CLOUD_TYPES.includes(cloudType);
};

/**
 * Check if a cloud type requires connection test
 * @param {string} cloudType - The cloud type to check
 * @returns {boolean} True if connection test is required
 */
export const requiresConnectionTest = (cloudType) => {
    return CONNECTION_TEST_CLOUD_TYPES.includes(cloudType);
};

/**
 * Check if a cloud type supports VM-level credentials (vSphere source)
 * @param {string} cloudType - The cloud type to check
 * @returns {boolean} True if VM credentials are supported
 */
export const supportsVMCredentials = (cloudType) => {
    return VM_CREDENTIALS_CLOUD_TYPES.includes(cloudType);
};

/**
 * Check if cloud type is AWS
 * @param {string} cloudType - The cloud type to check
 * @returns {boolean} True if AWS
 */
export const isAWS = (cloudType) => {
    return cloudType === CLOUD_TYPE.AWS;
};

/**
 * Check if cloud type is Azure
 * @param {string} cloudType - The cloud type to check
 * @returns {boolean} True if Azure
 */
export const isAzure = (cloudType) => {
    return cloudType === CLOUD_TYPE.AZURE;
};
/**
 * Check if cloud type is Azure Local
 * @param {string} cloudType - The cloud type to check
 * @returns {boolean} True if Azure Local
 */
export const isAzureLocal = (cloudType) => {
    return cloudType === CLOUD_TYPE.AZURE_LOCAL;
};

/**
 * Check if cloud type is GCP
 * @param {string} cloudType - The cloud type to check
 * @returns {boolean} True if GCP
 */
export const isGCP = (cloudType) => {
    return cloudType === CLOUD_TYPE.GCP;
};

/**
 * Check if cloud type is vSphere
 * @param {string} cloudType - The cloud type to check
 * @returns {boolean} True if vSphere
 */
export const isVSphere = (cloudType) => {
    return cloudType === CLOUD_TYPE.VSPHERE;
};

/**
 * Check if cloud type is OpenShift
 * @param {string} cloudType - The cloud type to check
 * @returns {boolean} True if OpenShift
 */
export const isOpenShift = (cloudType) => {
    return cloudType === CLOUD_TYPE.OPENSHIFT;
};
/**
 * Check if cloud type is OpenStack
 * @param {string} cloudType - The cloud type to check
 * @returns {boolean} True if OpenStack
 */
export const isOpenStack = (cloudType) => {
    return cloudType === CLOUD_TYPE.OPENSTACK;
};

/**
 * Check if cloud type is NC2
 * @param {string} cloudType - The cloud type to check
 * @returns {boolean} True if NC2
 */
export const isNC2 = (cloudType) => {
    return cloudType === CLOUD_TYPE.NC2;
};

/**
 * Check if cloud type is HPE VME
 * @param {string} cloudType - The cloud type to check
 * @returns {boolean} True if HPE VME
 */
export const isHPEVME = (cloudType) => {
    return cloudType === CLOUD_TYPE.HPE_VME;
};

/**
 * Check if cloud type is Hyper-V
 * @param {string} cloudType - The cloud type to check
 * @returns {boolean} True if Hyper-V
 */
export const isHyperV = (cloudType) => {
    return cloudType === CLOUD_TYPE.HYPERV;
};

/**
 * Get default URL for a cloud type
 * @param {string} cloudType - The cloud type
 * @returns {string|null} The default URL or null
 */
export const getDefaultUrl = (cloudType) => {
    const defaults = {
        [CLOUD_TYPE.AWS]: 'https://aws.amazon.com',
        [CLOUD_TYPE.AZURE]: 'https://portal.azure.com',
        [CLOUD_TYPE.AZURE_LOCAL]: 'https://portal.azure.com',
        [CLOUD_TYPE.GCP]: 'https://cloud.google.com',
    };
    return defaults[cloudType] || null;
};

/**
 * Get cloud type label
 * @param {string} cloudType - The cloud type value
 * @returns {string} The cloud type label
 */
export const getCloudTypeLabel = (cloudType) => {
    const cloudTypeObj = CLOUD_TYPES.find((ct) => ct.value === cloudType);
    return cloudTypeObj ? cloudTypeObj.label : cloudType;
};
